import { DeleteOutlined, PaperClipOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Space, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { urlAdmin } from '../../constants';
import { validateMessages } from '../../helpers/common';
import { KeKhaiTaiSan, TaiLieuKeKhaiTaiSan } from '../../models/taisan/KeKhaiTaiSan';
import { layChiTietKeKhai, luuKeKhaiTaiSan } from '../../services/keKhaiTaiSan';

const { TextArea } = Input;
const { Text } = Typography;

interface ModalLuuKeKhaiTaiSanProps {
    open: boolean;
    idKeKhai: number | null;
    onClose: () => void;
    onSuccess: () => void;
}

const ModalLuuKeKhaiTaiSan = ({ open, idKeKhai, onClose, onSuccess }: ModalLuuKeKhaiTaiSanProps) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [dsTaiLieuHienTai, setDsTaiLieuHienTai] = useState<TaiLieuKeKhaiTaiSan[]>([]);
    const [idsTaiLieuXoa, setIdsTaiLieuXoa] = useState<number[]>([]);

    useEffect(() => {
        if (open) {
            form.resetFields();
            setFileList([]);
            setDsTaiLieuHienTai([]);
            setIdsTaiLieuXoa([]);

            if (idKeKhai && idKeKhai > 0) {
                loadChiTiet(idKeKhai);
            } else {
                form.setFieldsValue({
                    namHinhThanh: new Date().getFullYear(),
                    giaTriTaiSan: 0
                });
            }
        }
    }, [open, idKeKhai]);

    const loadChiTiet = async (id: number) => {
        setIsLoading(true);
        try {
            const data = await layChiTietKeKhai(id);
            if (data) {
                form.setFieldsValue({
                    tenTaiSan: data.tenTaiSan,
                    namHinhThanh: data.namHinhThanh,
                    nguonGoc: data.NguonGoc || (data as any).nguonGoc,
                    giaTriTaiSan: data.giaTriTaiSan,
                    ghiChu: data.ghiChu,
                });
                setDsTaiLieuHienTai(data.dsTaiLieu || []);
            }
        } catch (error: any) {
            toast.error(error.message || "Lỗi tải chi tiết kê khai tài sản");
        } finally {
            setIsLoading(false);
        }
    };

    const handleXoaFileCu = (idFile: number) => {
        setIdsTaiLieuXoa(prev => [...prev, idFile]);
        setDsTaiLieuHienTai(prev => prev.filter(f => f.id !== idFile));
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setIsSaving(true);

            const formData = new FormData();
            if (idKeKhai && idKeKhai > 0) {
                formData.append("Id", idKeKhai.toString());
            }
            formData.append("TenTaiSan", values.tenTaiSan.trim());
            formData.append("NamHinhThanh", values.namHinhThanh.toString());
            formData.append("NguonGoc", values.nguonGoc.trim());
            formData.append("GiaTriTaiSan", (values.giaTriTaiSan || 0).toString());
            if (values.ghiChu) {
                formData.append("GhiChu", values.ghiChu.trim());
            }

            // Tệp đính kèm mới
            fileList.forEach(file => {
                if (file.originFileObj) {
                    formData.append("Files", file.originFileObj);
                }
            });

            // IDs tệp cần xóa
            idsTaiLieuXoa.forEach(idXoa => {
                formData.append("IdsTaiLieuXoa", idXoa.toString());
            });

            const res = await luuKeKhaiTaiSan(formData);
            toast.success(res.thongBao || (idKeKhai ? "Cập nhật kê khai thành công" : "Thêm mới kê khai thành công"));
            onSuccess();
            onClose();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            toast.error(error.message || "Lưu kê khai tài sản thất bại");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            title={idKeKhai && idKeKhai > 0 ? "Cập nhật kê khai tài sản" : "Thêm mới kê khai tài sản"}
            open={open}
            onCancel={onClose}
            width={750}
            footer={[
                <Button key="back" onClick={onClose}>
                    Đóng
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={isSaving}
                    onClick={handleSave}
                >
                    {idKeKhai && idKeKhai > 0 ? "Cập nhật" : "Lưu kê khai"}
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                validateMessages={validateMessages}
                disabled={isLoading}
            >
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Tên tài sản"
                            name="tenTaiSan"
                            rules={[{ required: true, message: "Vui lòng nhập tên tài sản" }]}
                        >
                            <Input placeholder="Nhập tên tài sản (VD: Nhà ở, Xe ô tô, Quyền sử dụng đất...)" maxLength={255} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Năm hình thành"
                            name="namHinhThanh"
                            rules={[{ required: true, message: "Vui lòng nhập năm hình thành" }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1900}
                                max={2100}
                                placeholder="VD: 2023"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Giá trị tài sản (VNĐ)"
                            name="giaTriTaiSan"
                            rules={[{ required: true, message: "Vui lòng nhập giá trị tài sản" }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                                placeholder="Nhập giá trị tài sản"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Nguồn gốc"
                            name="nguonGoc"
                            rules={[{ required: true, message: "Vui lòng nhập nguồn gốc tài sản" }]}
                        >
                            <Input placeholder="Nhập nguồn gốc (VD: Mua mới, Được tặng cho, Thừa kế, Tích lũy từ thu nhập...)" maxLength={500} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label="Ghi chú / Mô tả chi tiết"
                            name="ghiChu"
                        >
                            <TextArea rows={3} placeholder="Thông tin chi tiết thêm về tài sản (nếu có)" maxLength={1000} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Danh sách tệp đính kèm hiện có (khi chỉnh sửa) */}
                {dsTaiLieuHienTai && dsTaiLieuHienTai.length > 0 && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={24}>
                            <Text strong>Tệp đính kèm hiện tại:</Text>
                            <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                                {dsTaiLieuHienTai.map(file => {
                                    const fileUrl = urlAdmin + (file.duongDan || '').replace(/\\/g, '/');
                                    return (
                                        <div
                                            key={file.id}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '4px 0',
                                                borderBottom: '1px dashed #d9d9d9'
                                            }}
                                        >
                                            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                                <PaperClipOutlined style={{ marginRight: 6 }} />
                                                {file.tenTep}
                                            </a>
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => file.id && handleXoaFileCu(file.id)}
                                                title="Xóa tệp này"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                )}

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Đính kèm tệp mới (có thể đính kèm nhiều tệp)">
                            <Upload
                                fileList={fileList}
                                multiple
                                beforeUpload={() => false}
                                onChange={({ fileList }) => setFileList(fileList)}
                            >
                                <Button icon={<UploadOutlined />}>Chọn tệp đính kèm</Button>
                            </Upload>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                Hỗ trợ các định dạng tài liệu, hình ảnh (PDF, DOCX, XLSX, JPG, PNG...)
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ModalLuuKeKhaiTaiSan;
